/* eslint-disable unicorn/filename-case -- kebab-case per docs/conventions/core.md: this file
   exports a hook, not a React component. Matches the same exemption on
   table-internals/use-row-keyboard-navigation.ts. */
import type { ExpandedState, OnChangeFn, RowData } from '@tanstack/react-table';
import * as React from 'react';

import { collectExpandableRowIds } from './collect-expandable-row-ids.js';
import type { TreeRow } from './DimeTreeTable.types.js';

interface ExpandAllTreeRowsOptions<TData extends RowData> {
  coreRows: readonly TreeRow<TData>[];
  // Typed to match table.setExpanded exactly (the intended caller) rather than the narrower
  // Record-only shape DimeNestedTable's onExpandedChange prop uses -- ExpandedState's `true` "all
  // expanded" sentinel is a real value this hook's own caller (index.tsx, via defaultAllExpanded's
  // internal seed) can hand back in, so the updater below has to accept and normalize it.
  onExpandedChange: OnChangeFn<ExpandedState>;
}

interface ExpandAllTreeRows {
  expandAllRows: () => void;
  collapseAllRows: () => void;
}

/**
 * Expand/collapse every node in the whole tree — there is no per-page scoping here, unlike
 * DimeNestedTable's use-expand-all-rows, because DimeTreeTable has no row pagination (see
 * DimeTreeTable.md for why: you can't page a tree by row without cutting a branch mid-way).
 *
 * Deliberately NOT shared with DimeNestedTable's use-expand-all-rows, even though both hooks
 * ultimately do "expand a set of rows in one update": that hook's 126 lines are ~70% lazy-fetch
 * request/cache orchestration (onExpandRow fan-out, requested-id tracking, failure retry) that a
 * fully-materialized tree has no use for. Unifying them behind a `mode` flag would produce one
 * harder-to-read function instead of two honest, independently-testable ones.
 */
export function useExpandAllTreeRows<TData extends RowData>({
  coreRows,
  onExpandedChange,
}: ExpandAllTreeRowsOptions<TData>): ExpandAllTreeRows {
  const expandAllRows = React.useCallback(() => {
    const expandableIds = collectExpandableRowIds(coreRows);
    // Exactly ONE onExpandedChange call carrying every id. N sequential single-row updates would
    // each be computed from the same pre-update state under a controlled `expanded` prop and
    // clobber one another, leaving only the last node expanded — the same pitfall
    // use-expand-all-rows.ts:34-36 documents for DimeNestedTable.
    onExpandedChange((current) => {
      // `current === true` means "everything already expanded" -- expandableIds already covers
      // every expandable id in the whole tree, so rebuilding an explicit record from it is
      // equivalent, not a narrowing of what's already open.
      const currentRecord = current === true ? {} : current;
      const next = { ...currentRecord };
      for (const id of expandableIds) next[id] = true;
      return next;
    });
  }, [coreRows, onExpandedChange]);

  const collapseAllRows = React.useCallback(() => {
    // No per-page scoping to preserve: the whole tree is always in `data`, so a full reset is the
    // whole answer, unlike DimeNestedTable's collapseAllRows which deletes only the current page's
    // ids to avoid discarding a controlled `expanded` prop's state for parents on other pages.
    onExpandedChange({});
  }, [onExpandedChange]);

  return { expandAllRows, collapseAllRows };
}
